import { Link } from 'react-router-dom';
import { formatShipToAddress, type ShipToAddress } from '@mypoz/shared';
import { useTranslation } from '@mypoz/i18n';
import { Card, Icon } from '@mypoz/ui';

import { ShipToAddressBlock } from '@/features/warehouses/index.js';
import { actionLinkClass } from '@/lib/styles.js';
import { useCopyToClipboard } from '@/lib/clipboard.js';

export interface ShipToAddressCardProps {
  /** Undefined while the warehouse list is loading, and when it could not be loaded at all. */
  address: ShipToAddress | undefined;
  /**
   * Where the warehouse list has got to (MYP-30).
   *
   * Passed in rather than inferred from `address` being undefined, because "still loading" and
   * "there is no warehouse" look identical from here and must not read the same: telling a
   * customer their account has no ship-to address, a second before one appears, is worse than
   * saying nothing for that second.
   */
  status: 'loading' | 'ready' | 'error';
  customerCode: string;
  allWarehousesHref: string;
}

/**
 * The Dashboard's ship-to card: one warehouse, the address, and the two things anyone opens the
 * Dashboard to do with it — take a copy, or go looking for a different warehouse.
 *
 * What lands on the clipboard is built by `formatShipToAddress`, not scraped from what is on
 * screen. The two are deliberately different: this card is arranged to be read, the clipboard
 * string is arranged to be pasted into a shopping site's address box. Keeping the string in a
 * tested pure function is what stops the highest-risk part of this screen from depending on JSX.
 */
export function ShipToAddressCard({
  address,
  status,
  customerCode,
  allWarehousesHref,
}: ShipToAddressCardProps) {
  const { t } = useTranslation();
  const { copied, copy } = useCopyToClipboard();

  if (!address) {
    return (
      <Card className="flex h-full flex-col">
        <h2 className="text-base font-semibold text-ink">{t('dashboard.shipTo.emptyTitle')}</h2>
        {status === 'loading' ? (
          <div aria-hidden="true" className="mt-4 animate-pulse space-y-2">
            <div className="h-4 w-full rounded bg-surface-alt" />
            <div className="h-4 w-2/3 rounded bg-surface-alt" />
          </div>
        ) : (
          <p className="mt-2 text-sm text-muted">
            {status === 'error' ? t('dashboard.shipTo.failed') : t('dashboard.shipTo.none')}
          </p>
        )}
      </Card>
    );
  }

  return (
    <Card className="flex h-full flex-col">
      {/*
        The warehouse's own name, not a city. The endpoint sends a numeric city id and nothing to
        resolve it against for half the warehouses, and a heading that named the wrong city would
        be worse than one that names the warehouse the address actually belongs to.
      */}
      <h2 className="text-base font-semibold text-ink">
        {t('dashboard.shipTo.title', { location: address.label })}
      </h2>

      <div className="mt-4">
        <ShipToAddressBlock address={address} customerCode={customerCode} />
      </div>

      <div className="mt-auto flex flex-wrap items-center gap-x-6 gap-y-1 pt-5">
        <button
          type="button"
          onClick={() => void copy(formatShipToAddress(address, customerCode))}
          className={actionLinkClass()}
        >
          {copied ? t('dashboard.shipTo.copied') : t('dashboard.shipTo.copy')}
          <Icon name={copied ? 'check' : 'content_copy'} size={17} />
        </button>

        <Link to={allWarehousesHref} className={actionLinkClass()}>
          {t('dashboard.shipTo.allWarehouses')}
          <Icon name="arrow_forward" size={17} />
        </Link>
      </div>

      {/*
        Swapping the button's own label is a silent change to a screen reader, which announces a
        control when it is focused and not again afterwards. This region is what actually says
        the copy worked.
      */}
      <span aria-live="polite" className="sr-only">
        {copied ? t('dashboard.shipTo.copied') : ''}
      </span>
    </Card>
  );
}
